import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Zap } from "lucide-react";
import { linkitApi, formatApiErrorDetail } from "../api";
import { useLinkitAuth } from "../AuthContext";

// Standalone route hit by the "Verify email" button in the transactional email.
// Reads ?token=..., posts to /auth/verify-email, then either:
//   - refreshes the auth session (if the user is signed in) so email_verified
//     flips on immediately, OR
//   - shows a "Go to login" CTA for signed-out visitors.
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, ok: false, message: "" });
  const { user, refresh } = useLinkitAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!token) {
      setState({ loading: false, ok: false, message: "This link is missing its token. Try the link from your email again." });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await linkitApi.post("/auth/verify-email", { token });
        if (cancelled) return;
        setState({ loading: false, ok: true, message: data.message || "Email verified!" });
        // If the user is already signed in in this tab, refresh /auth/me so
        // the banner disappears without a page reload.
        try { await refresh(); } catch (e) { /* ignore */ }
      } catch (err) {
        if (cancelled) return;
        setState({
          loading: false,
          ok: false,
          message: formatApiErrorDetail(err.response?.data?.detail) || "Couldn't verify. Try again.",
        });
      }
    })();
    return () => { cancelled = true; };
  }, [token, refresh]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0b0c10] px-6 text-white">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
        <Link to="/create" className="mb-6 inline-flex items-center gap-2 text-lg font-black">
          <Zap size={20} fill="white" /> Linkit
        </Link>
        {state.loading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={32} className="animate-spin text-indigo-400" />
            <p className="text-sm text-white/60">Verifying your email…</p>
          </div>
        ) : state.ok ? (
          <div className="flex flex-col items-center gap-3">
            <CheckCircle2 size={40} className="text-emerald-400" />
            <h1 className="text-xl font-black" data-testid="verify-ok">Email verified</h1>
            <p className="text-sm text-white/60">{state.message}</p>
            <button
              onClick={() => nav(user ? "/dashboard" : "/login")}
              data-testid="verify-continue"
              className="mt-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-bold hover:bg-indigo-400"
            >
              {user ? "Go to dashboard" : "Continue to login"}
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <XCircle size={40} className="text-red-400" />
            <h1 className="text-xl font-black" data-testid="verify-fail">Couldn't verify</h1>
            <p className="text-sm text-white/60">{state.message}</p>
            <Link to={user ? "/dashboard" : "/login"} className="mt-2 rounded-full border border-white/20 px-5 py-2 text-sm font-semibold hover:bg-white/5">
              Back to Linkit
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
